/**
 * Shared 16 px line icons — stroke = currentColor, 1.5 px, round caps — the
 * ONE drawing contract every icon-only control in Kola draws from, so the
 * pills, the board menu, the tool rail, the selection toolbar and the panels
 * read as a single set. Inline SVG rather than an icon font or emoji: emoji
 * render differently per OS and cannot take the theme's ink, and a font is a
 * network round trip for a dozen glyphs.
 *
 * Every icon-only control also carries a `data-tip` hover label (app.css):
 * an icon never has to be guessed at. See ui/CloseButton for the pattern.
 */

export const svgProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 16 16",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

/** × — close a panel, dialog or popover. */
export const iconClose = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M4 4l8 8" />
    <path d="M12 4l-8 8" />
  </svg>
);

/** ⋯ — a menu of board-level actions. */
export const iconDots = (
  <svg {...svgProps} aria-hidden="true">
    <circle cx="3.5" cy="8" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="8" cy="8" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12.5" cy="8" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);

/** A window with a side panel — the comments panel toggle. */
export const iconPanel = (
  <svg {...svgProps} aria-hidden="true">
    <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
    <path d="M9.5 2.5v11" />
  </svg>
);

/** Board agent (F-144): the two-star sparkle every AI surface here uses. */
export const iconAgent = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M6.2 1.5 7.4 4.6 10.5 5.8 7.4 7 6.2 10.1 5 7 1.9 5.8 5 4.6Z" />
    <path d="M11.8 8.6 12.5 10.5 14.4 11.2 12.5 11.9 11.8 13.8 11.1 11.9 9.2 11.2 11.1 10.5Z" />
  </svg>
);

/** Design theme: overlapping swatches — a palette, which is what it opens with. */
export const iconDesign = (
  <svg {...svgProps} aria-hidden="true">
    <circle cx="6" cy="6" r="3.5" />
    <circle cx="10" cy="10" r="3.5" />
  </svg>
);

/** Thumbnails: a 16:9 frame with a face in it — what the panel makes. */
export const iconThumbnail = (
  <svg {...svgProps} aria-hidden="true">
    <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" />
    <circle cx="6" cy="7.5" r="1.6" />
    <path d="M3.5 12.5c.4-1.8 1.4-2.7 2.5-2.7s2.1.9 2.5 2.7" />
    <path d="M10 7h3" />
    <path d="M10 9.5h3" />
  </svg>
);

/** Rows grouped into two bracketed blocks — a script divided into sections. */
export const iconSections = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M5.5 3h8" />
    <path d="M5.5 6h8" />
    <path d="M5.5 10h8" />
    <path d="M5.5 13h8" />
    <path d="M3 2.5v4.5" />
    <path d="M3 9.5v4" />
  </svg>
);

/** Latin "A" + a CJK-style glyph: "this text, in another script". */
export const iconTranslate = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M1.8 10.4 4.5 3.8l2.7 6.6" />
    <path d="M2.8 8.4h3.4" />
    <path d="M11.5 7.3v1.2" />
    <path d="M8.7 9.9h5.6" />
    <path d="m9.7 14.3 3.8-3.5" />
    <path d="m9.7 10.8 3.8 3.5" />
  </svg>
);

/** ← — back to the boards list. */
export const iconBack = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M9.5 3.5 5 8l4.5 4.5" />
    <path d="M5 8h8" />
  </svg>
);

/** ↺ — undo. */
export const iconUndo = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M4 6.5h5.5a3 3 0 0 1 0 6H7" />
    <path d="M6.5 4 4 6.5 6.5 9" />
  </svg>
);

/** ↻ — redo. */
export const iconRedo = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M12 6.5H6.5a3 3 0 0 0 0 6H9" />
    <path d="M9.5 4 12 6.5 9.5 9" />
  </svg>
);

/** Two chain links — copy a share link. */
export const iconLink = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M6.5 9.5 9.5 6.5" />
    <path d="m7.2 4.8 1-1a2.5 2.5 0 0 1 3.5 3.5l-1 1" />
    <path d="m8.8 11.2-1 1a2.5 2.5 0 0 1-3.5-3.5l1-1" />
  </svg>
);

/** Arrow into a tray — import a file. */
export const iconImport = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M8 2.5v8" />
    <path d="M5 7.5 8 10.5l3-3" />
    <path d="M2.5 13.5h11" />
  </svg>
);

/**
 * Arrow down into a tray — save a file to disk. The mirror of `iconImport`,
 * and shared by every download control (the F-27 selection toolbar's, the
 * design panel's).
 */
export const iconDownload = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M8 2.5v7.5M5 7.5 8 10.5l3-3" />
    <path d="M2.5 11v1.5a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V11" />
  </svg>
);

/** Two sheets — copy to the clipboard. */
export const iconCopy = (
  <svg {...svgProps} aria-hidden="true">
    <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
    <path d="M10.5 3.5a1.5 1.5 0 0 0-1.5-1.5H4a1.5 1.5 0 0 0-1.5 1.5V9a1.5 1.5 0 0 0 1.5 1.5" />
  </svg>
);

/** Arrow out of a tray — export a file. */
export const iconExport = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M8 10.5v-8" />
    <path d="M5 5.5 8 2.5l3 3" />
    <path d="M2.5 13.5h11" />
  </svg>
);

/** Rows with a small × — delete the empty ones. */
export const iconRowsClear = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M2.5 4h11" />
    <path d="M2.5 8h11" />
    <path d="M2.5 12h5" />
    <path d="m10.5 10.5 3 3" />
    <path d="m13.5 10.5-3 3" />
  </svg>
);

/** A table — the Grid view. */
export const iconGrid = (
  <svg {...svgProps} aria-hidden="true">
    <rect x="2.5" y="2.5" width="11" height="11" rx="1.5" />
    <path d="M2.5 7h11" />
    <path d="M7 2.5v11" />
  </svg>
);

/**
 * Lines of prose in two spaced groups — copying the script with a blank line
 * between every line (the teleprompter copy).
 */
export const iconTeleprompter = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M3 2.5h10" />
    <path d="M3 5h7" />
    <path d="M3 11h10" />
    <path d="M3 13.5h7" />
  </svg>
);

/** A folder with a sync arrow — the F-138 storyboard sync folder. */
export const iconSyncFolder = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M2 4.5A1.5 1.5 0 0 1 3.5 3h3l1.5 1.5h4.5A1.5 1.5 0 0 1 14 6v6a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 12z" />
    <path d="M6 9.5a2 2 0 0 1 3.5-1.3M10 10.5a2 2 0 0 1-3.5 1.3" />
    <path d="M9.5 7v1.5H8M6.5 13v-1.5H8" />
  </svg>
);

/** Lines of prose — the Script view. */
export const iconScript = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M3 3.5h10" />
    <path d="M3 6.5h10" />
    <path d="M3 9.5h7" />
    <path d="M3 12.5h5" />
  </svg>
);

/** Music details, kept compact until expanded. */
export const iconMusic = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M6 11V3.5l7-1.5v7.5M6 6l7-1.5" />
    <ellipse cx="4" cy="11.5" rx="2" ry="1.5" />
    <ellipse cx="11" cy="10" rx="2" ry="1.5" />
  </svg>
);

/** A closed folder — a folder row in a file list. */
export const iconFolder = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M2 4.5A1.5 1.5 0 0 1 3.5 3h3l1.5 1.5h4.5A1.5 1.5 0 0 1 14 6v6a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 12z" />
  </svg>
);

/** A page with lines — a text file row in a file list. */
export const iconFile = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M4 1.5h5L12.5 5v9a.5.5 0 0 1-.5.5H4a.5.5 0 0 1-.5-.5V2a.5.5 0 0 1 .5-.5z" />
    <path d="M9 1.5V5h3.5" />
    <path d="M5.5 8h5M5.5 10.5h5" />
  </svg>
);
